import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { fmtNum } from "../lib/format";
import { List } from "lucide-react";

function RoomDetailDialog({ dateFrom, dateTo, item, onClose }) {
  const [data, setData] = useState(null);
  const [showTxns, setShowTxns] = useState(false);
  useEffect(() => {
    if (!item) return;
    setData(null);
    api.get(`/assets/reports/consumables-consumption/room-detail?date_from=${dateFrom}&date_to=${dateTo}&item_id=${item.item_id}`)
      .then(({ data }) => setData(data));
  }, [item, dateFrom, dateTo]);

  return (
    <Dialog open={!!item} onOpenChange={o => !o && onClose()}>
      <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto" data-testid="room-detail-dialog">
        <DialogHeader>
          <DialogTitle>Room-wise Allocation — {item?.name} <span className="text-slate-400 font-normal">({dateFrom === dateTo ? dateFrom : `${dateFrom} → ${dateTo}`})</span></DialogTitle>
          <DialogDescription>Ledger-derived allocation, consumption and current balance per room for the selected month.</DialogDescription>
        </DialogHeader>
        {!data ? <div className="py-8 text-center text-slate-400">Loading…</div> : (
          <div className="space-y-4">
            <div className="overflow-x-auto">
              <table className="vr-table" data-testid="room-detail-table">
                <thead><tr>
                  <th>Room</th><th>Type</th>
                  <th className="num">Issued</th><th className="num">Returned</th>
                  <th className="num">Consumed</th><th className="num">Loss/Wastage</th>
                  <th className="num">Net Allocation</th><th className="num">Current Balance</th>
                </tr></thead>
                <tbody>
                  {data.rows.map(r => {
                    const special = r.key === "__POOL__" || r.key === "__UNASSIGNED__";
                    return (
                      <tr key={r.key} className={special ? "bg-amber-50" : ""}>
                        <td className={special ? "font-semibold text-amber-800" : "font-medium"}>{special ? r.room_number : `Room ${r.room_number}`}</td>
                        <td className="text-xs text-slate-500">{r.room_type || "—"}</td>
                        <td className="num">{fmtNum(r.issued)}</td>
                        <td className="num">{fmtNum(r.returned)}</td>
                        <td className="num font-semibold">{fmtNum(r.consumed)}</td>
                        <td className={`num ${r.loss ? "text-red-700" : ""}`}>{fmtNum(r.loss)}</td>
                        <td className="num font-semibold">{fmtNum(r.net_allocation)}</td>
                        <td className="num">{fmtNum(r.current_balance)}</td>
                      </tr>
                    );
                  })}
                  {data.rows.length === 0 && <tr><td colSpan={8} className="text-center text-slate-400 py-4">No room activity or balance for this month.</td></tr>}
                </tbody>
                <tfoot><tr className="bg-slate-900 text-white font-semibold">
                  <td colSpan={2}>Totals</td>
                  <td className="num" data-testid="rd-total-issued">{fmtNum(data.totals.issued)}</td>
                  <td className="num" data-testid="rd-total-returned">{fmtNum(data.totals.returned)}</td>
                  <td className="num" data-testid="rd-total-consumed">{fmtNum(data.totals.consumed)}</td>
                  <td className="num" data-testid="rd-total-loss">{fmtNum(data.totals.loss)}</td>
                  <td className="num">{fmtNum(data.totals.net_allocation)}</td>
                  <td className="num">{fmtNum(data.totals.current_balance)}</td>
                </tr></tfoot>
              </table>
            </div>
            <p className="text-xs text-slate-400">Net Monthly Allocation = Issued − Returned − Consumed − Approved Loss/Wastage. Current Balance is the live ledger balance (carried forward across months). Shared-pool and unassigned movements are shown separately and never attributed to a specific room.</p>

            <div>
              <Button variant="outline" size="sm" onClick={() => setShowTxns(s => !s)} data-testid="toggle-txn-history">
                <List className="w-4 h-4 mr-1.5" /> {showTxns ? "Hide" : "Show"} transaction history ({data.transactions.length})
              </Button>
              {showTxns && (
                <div className="overflow-x-auto mt-2">
                  <table className="vr-table" data-testid="txn-history-table">
                    <thead><tr><th>Date/Time</th><th className="num">Qty</th><th>Type</th><th>Room</th><th>Entered By</th><th>Reference</th><th>Remarks</th></tr></thead>
                    <tbody>
                      {data.transactions.map((t, i) => (
                        <tr key={t.txn_id || `${t.date}-${i}`}>
                          <td className="text-xs">{t.date ? new Date(t.date).toLocaleString() : "—"}</td>
                          <td className={`num ${t.qty < 0 ? "text-red-700" : "text-emerald-700"}`}>{t.qty > 0 ? "+" : ""}{fmtNum(t.qty)}</td>
                          <td className="text-xs">{(t.txn_type || "").replace(/^asset_/, "").replace(/_/g, " ")}</td>
                          <td className="text-xs">{t.room}</td>
                          <td className="text-xs">{t.entered_by || "—"}</td>
                          <td className="text-xs">{t.reference || "—"}</td>
                          <td className="text-xs">{t.remarks || "—"}</td>
                        </tr>
                      ))}
                      {data.transactions.length === 0 && <tr><td colSpan={7} className="text-center text-slate-400 py-3">No transactions this month.</td></tr>}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function AssetConsumablesConsumption({ dateFrom, dateTo }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detailItem, setDetailItem] = useState(null);

  useEffect(() => {
    setLoading(true);
    api.get(`/assets/reports/consumables-consumption?date_from=${dateFrom}&date_to=${dateTo}`)
      .then(({ data }) => setRows(data.rows || []))
      .finally(() => setLoading(false));
  }, [dateFrom, dateTo]);

  return (
    <Card className="p-0 overflow-hidden">
      <div className="p-3 text-xs text-slate-500 border-b border-slate-100 space-y-1">
        <div>Consumable-tracking items only. Moving a kettle, tray, pump, laptop or chair is an asset movement, not consumption — it never appears here.</div>
        <div>
          <span className="font-medium text-slate-600">Issued / Returned</span> are internal location moves (Store ↔ Room) and do <span className="font-medium">not</span> change the owned total.
          Closing = Opening + Purchased − Consumption − Approved Loss/Wastage.
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="vr-table" data-testid="consumables-consumption-table">
          <thead><tr>
            <th>Department</th><th>Category</th><th>SKU</th><th>Item</th><th>UOM</th>
            <th className="num">Opening</th><th className="num">Purchased</th><th className="num">Issued</th>
            <th className="num">Returned</th><th className="num">Consumption</th><th className="num">Loss/Wastage</th>
            <th className="num">Baseline Adjustment</th>
            <th className="num">Closing</th>
            <th className="num">Avg/Day</th><th className="num">Reorder</th><th className="num">Days Left</th>
            <th className="no-print"></th>
          </tr></thead>
          <tbody>
            {rows.map(r => (
              <tr key={r.item_id}>
                <td className="text-xs">{r.department || "—"}</td>
                <td className="text-xs">{r.category || "—"}</td>
                <td className="font-mono text-xs">{r.sku}</td>
                <td>{r.name}</td>
                <td className="text-xs">{r.base_uom || <span className="text-amber-700">UOM Setup Required</span>}</td>
                <td className="num">{fmtNum(r.opening)}</td>
                <td className="num">{fmtNum(r.purchased)}</td>
                <td className="num text-slate-500">{fmtNum(r.issued)}</td>
                <td className="num text-slate-500">{fmtNum(r.returned)}</td>
                <td className="num font-semibold">{fmtNum(r.consumption)}</td>
                <td className={`num ${r.loss ? "text-red-700" : ""}`}>{fmtNum(r.loss)}</td>
                <td className="num text-amber-800">{fmtNum(r.baseline_adjustment)}</td>
                <td className="num font-semibold">{fmtNum(r.closing)}</td>
                <td className="num text-slate-500">{r.avg_daily}</td>
                <td className="num text-slate-500">{r.reorder_level}</td>
                <td className="num">{r.days_left == null ? "—" : r.days_left}</td>
                <td className="no-print">
                  <Button size="sm" variant="outline" onClick={() => setDetailItem(r)} data-testid={`details-btn-${r.sku}`}>Details</Button>
                </td>
              </tr>
            ))}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={17} className="text-center text-slate-500 py-5">No consumable items.</td></tr>
            )}
          </tbody>
        </table>
      </div>
      <RoomDetailDialog dateFrom={dateFrom} dateTo={dateTo} item={detailItem} onClose={() => setDetailItem(null)} />
    </Card>
  );
}
